import type { Surface } from '../shared/types';
import { BLOCK_WIDTH } from '../shared/sceneDimensions';

export interface WallView { x: number; y: number; zoom: number }
export interface ViewPoint { x: number; y: number }
export const MIN_WALL_ZOOM = .5;
export const MAX_WALL_ZOOM = 8;
export function wallViewSize(surface: Surface, aspect: number, zoom: number, bottomInset = 0) {
  const height = Math.max((surface.height + 32) / (1 - bottomInset), (surface.width + 32) / aspect) / Math.max(MIN_WALL_ZOOM, Math.min(MAX_WALL_ZOOM, zoom));
  return { width: height * aspect, height };
}
export function clampWallView(surface: Surface, aspect: number, view: WallView, bottomInset = 0, blockStart = surface.blockId * BLOCK_WIDTH, occludedBottom=bottomInset): WallView {
  const zoom = Math.max(MIN_WALL_ZOOM, Math.min(MAX_WALL_ZOOM, view.zoom));
  const size = wallViewSize(surface, aspect, zoom, bottomInset), left = blockStart + surface.x;
  const clamp = (value: number, start: number, length: number, visible: number) => visible >= length
    ? start + (length - visible) / 2
    : Math.max(start, Math.min(start + length - visible, value));
  const baselineHeight=size.height*(1-bottomInset),visibleHeight=size.height*(1-Math.max(bottomInset,Math.min(.95,occludedBottom)));
  const minY=baselineHeight>=surface.height?surface.y+(surface.height-baselineHeight)/2:surface.y;
  const maxY=Math.max(minY,surface.y+surface.height-visibleHeight);
  return { zoom, x: clamp(view.x, left, surface.width, size.width), y:Math.max(minY,Math.min(maxY,view.y)) };
}
export function initialWallView(surface: Surface, aspect: number, bottomInset = 0, blockStart = surface.blockId * BLOCK_WIDTH): WallView {
  return clampWallView(surface, aspect, { x: 0, y: 0, zoom: 1 }, bottomInset, blockStart);
}
export function zoomWallView(surface: Surface, aspect: number, view: WallView, zoom: number, anchor: ViewPoint = { x: .5, y: .5 }, current = anchor, bottomInset = 0, blockStart = surface.blockId * BLOCK_WIDTH,occludedBottom=bottomInset): WallView {
  const before = wallViewSize(surface, aspect, view.zoom, bottomInset), after = wallViewSize(surface, aspect, zoom, bottomInset);
  return clampWallView(surface, aspect, { zoom, x: view.x + anchor.x * before.width - current.x * after.width, y: view.y + anchor.y * before.height - current.y * after.height }, bottomInset, blockStart, occludedBottom);
}
export function panWallView(surface: Surface, aspect: number, view: WallView, dx: number, dy: number, blockStart = surface.blockId * BLOCK_WIDTH): WallView {
  const size = wallViewSize(surface, aspect, view.zoom);
  return clampWallView(surface, aspect, { ...view, x: view.x - dx * size.width, y: view.y - dy * size.height }, 0, blockStart);
}
